<template>
  <div id="listWidget" class="mb-4">
    <!-- 단계별 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-list-ol me-2 text-primary"></i>
        <span class="instruction-title">전동커튼 등록</span>
      </div>
      <div class="instruction-body">
        <div class="instruction-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <span>전동커튼을 포함시킬 방을 선택해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <span>전동커튼의 이름을 입력해주세요.</span>
          </div>
        </div>
        <div class="instruction-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <span>다음 버튼을 클릭해주세요.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- <hr class="mb-3" /> -->

    <div class="row" v-if="zone?.groups.length > 0">
      <div class="col-xl-6 mb-3">
        <div class="text-muted fs-12px mb-2"><b>방 선택</b></div>
        <div class="list-group" style="border-radius: 1.2rem">
          <ListGroupItem
            style="border-radius: 1.2rem; margin-bottom: 4px;"
            v-for="(group, index) in zone?.groups"
            :key="group._id"
          >
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`gridCheck${index}`"
                :value="group._id"
                @change="onSelectGroupChange"
                :checked="selectedGroup == group._id"
              />
              <label class="form-check-label" :for="`gridCheck${index}`">
                {{ group.name }}
              </label>
            </div>
          </ListGroupItem>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-xl-6">
        <div class="text-muted fs-12px mb-2"><b>전동커튼 이름</b></div>
        <input
          type="text"
          class="form-control"
          placeholder="전동커튼 이름을 입력하세요."
          v-model="deviceName"
          maxlength="20"
        />
      </div>
    </div>

    <div class="row">
      <div class="col" />
      <div class="col" />
      <div class="col">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          style="width: 100%"
          @click="onRegistDeviceClick"
        >
          등록
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import Axios, { DeviceApi, GroupApi, ZoneApi } from '@/api';
import { useRouter, useRoute ,onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import { Template, DeviceType } from '@/common/constants';
import useMqttControl from '@/common/hooks/useMqttControl';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const props = defineProps(['handleNextStep', 'serial', 'mode']);
const router = useRouter();
const route = useRoute();
const store = useStore();
const zone = computed(() => store.getters['home/getCurrentZone']);
const selectedGroup = ref(undefined);
const deviceName = ref('');

const onSelectGroupChange = (e) => {
  selectedGroup.value = e.target.value;
}

const onRegistDeviceClick = () => {
  if (!selectedGroup.value) {
    emitter.emit('showPopAlert', {
      description: '전동커튼을 포함할 방을 선택해주세요.',
    });
    return;
  }

  if (!deviceName.value.trim()) {
    emitter.emit('showPopAlert', {
      description: '전동커튼 이름을 입력해주세요.',
    });
    return;
  }

  const controllerId = props.serial;//.slice(0, props.serial.indexOf("-"));
  requestAddDevice({
    groupId: selectedGroup.value,
    device: {
      controllerId: controllerId,
      name: deviceName.value.trim(),
      type: props.mode == 'm11_m31' ? DeviceType.Dooya : DeviceType.Curtain
    }
  });
}

const requestAddDevice = async (payload) => {
  try {
    const result = await Axios.post(
      GroupApi.REGIST_GROUP_DEVICES.replace(':id', payload.groupId),
      payload.device,
    );
    if (result.resultCode === '0000') {
      props.handleNextStep();
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '장치 추가 중 오류가 발생하였습니다.',
    });
  }
};
</script>

<style scoped>
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}
</style>